import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Grid, Card, Box, Typography, CircularProgress, Alert} from 'views/components';
import {Dropdown, DropdownMenuItem} from 'views/components';

import {Translate, translate} from '../Translate';

import Pagination from '@mui/material/Pagination';

const CardTable = (props) => {
  const {
    data,
    card: CardRender,
    isLoading,
    loadMore,
    hasMore,
    highlightedId,
    getItemId,
    cardsPerRow = 1,
    hidePagination = false,
  } = props;

  const safeCardsPerRow = Math.max(1, Math.floor(Number(cardsPerRow) || 1));

  const [paginatedData, setPaginatedData] = React.useState([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [page, setPage] = React.useState(1);

  // Refs for each card for scroll-to-highlight
  const [cardRefs, setCardRefs] = React.useState({});

  React.useEffect(() => {
    if (hidePagination) {
      return;
    }

    const endRecord = page * rowsPerPage;

    if (endRecord >= data.length && hasMore && !isLoading) {
      loadMore();
    }
  }, [hidePagination, page, rowsPerPage, data, hasMore, isLoading, loadMore]);

  React.useEffect(() => {
    if (!highlightedId || !data.length) return;

    // Find the index of the highlighted item
    const index = data.findIndex((item) => {
      return getItemId ? getItemId(item) === highlightedId : false;
    });

    if (index === -1) return;

    // Calculate which page it belongs to
    const newPage = Math.floor(index / rowsPerPage) + 1;

    // Only update if needed
    setPage((prevPage) => (prevPage !== newPage ? newPage : prevPage));
  }, [highlightedId]);

  React.useEffect(() => {
    if (!highlightedId) return;

    const el = cardRefs[highlightedId];
    if (!el) return;

    el.scrollIntoView({behavior: 'smooth', block: 'center'});
  }, [highlightedId, cardRefs[highlightedId]]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(1); // Reset to first page on rows per page change
  };

  const {startIndex, endIndex} = React.useMemo(() => {
    if (hidePagination) {
      return {startIndex: 0, endIndex: data.length};
    }

    const s = (page - 1) * rowsPerPage;
    const e = Math.min(s + rowsPerPage, data.length);
    return {startIndex: s, endIndex: e};
  }, [hidePagination, page, rowsPerPage, data.length]);

  React.useEffect(() => {
    const newPaginated = data.slice(startIndex, endIndex);
    setPaginatedData((prev) => {
      if (prev.length === newPaginated.length) {
        let equal = true;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i] !== newPaginated[i]) {
            equal = false;
            break;
          }
        }
        if (equal) return prev;
      }
      return newPaginated;
    });
  }, [data, startIndex, endIndex]);

  const Cards = React.useMemo(() => {
    const renderData = hidePagination ? data : paginatedData;

    return renderData.map((e, index) => {
      const itemId = getItemId ? getItemId(e) : index;
      const isHighlighted = itemId === highlightedId;

      return (
        <Grid item key={itemId} xs={1} md={1} sx={{display: 'flex'}}>
          <Card
            ref={(el) => {
              if (!el) return;
              setCardRefs((prev) => ({...prev, [itemId]: el}));
            }}
            sx={{
              'width': '100%',
              'boxShadow': isHighlighted ? 6 : undefined,
              'border': isHighlighted ? '2px solid' : undefined,
              'borderColor': isHighlighted ? 'primary.main' : undefined,
              '& > *': {
                height: '100%',
              },
            }}
          >
            <CardRender item={e} isHighlighted={isHighlighted} />
          </Card>
        </Grid>
      );
    });
  }, [hidePagination, data, paginatedData, highlightedId, CardRender]);

  return (
    <Stack
      spacing={2}
      sx={{width: '100%'}}
    >
      <Grid
        container
        columns={{xs: 1, md: safeCardsPerRow}}
        columnSpacing={2}
        rowSpacing={1}
        alignItems="stretch"
      >
        {Cards}
      </Grid>
      <Stack>
        {
          isLoading &&
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gridColumn: '1 / -1',
            }}
          >
            <CircularProgress />
          </Box>
        }
        {
          !isLoading && !data.length &&
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Alert severity="info" sx={{mt: 2}}>
              <Translate expression='table.NoResult'/>
            </Alert>
          </Box>
        }
        {
          Boolean(data.length) && !hidePagination &&
          <Stack
            alignItems='center'
            justifyContent="space-between"
            direction='row'
            sx={{
              width: '100%',
              display: 'flex',
            }}
          >
            <Typography variant="body2">
              <Translate
                expression={hasMore ? 'table.LabelDisplayedRowsMore' : 'table.LabelDisplayedRowsNoMore'}
                substitute={{from: startIndex + 1, to: endIndex, numOfRows: data.length}}
              />
            </Typography>
            <Pagination
              count={Math.ceil(data.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              showFirstButton
              showLastButton
            />
            <Box
              sx={{
                width: '100px',
              }}
            >
              <Dropdown
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                label={translate('table.RowsPerPage')}
                variant={'standard'}
              >
                <DropdownMenuItem value={25}>
                  25
                </DropdownMenuItem>
                <DropdownMenuItem value={50}>
                  50
                </DropdownMenuItem>
                <DropdownMenuItem value={100}>
                  100
                </DropdownMenuItem>
                <DropdownMenuItem value={500}>
                  500
                </DropdownMenuItem>
                <DropdownMenuItem value={1000}>
                  1000
                </DropdownMenuItem>
              </Dropdown>
            </Box>
          </Stack>
        }
      </Stack>
    </Stack>
  );
};

CardTable.propTypes = {
  data: PropTypes.array.isRequired,
  card: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
  ]).isRequired,
  isLoading: PropTypes.bool,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  highlightedId: PropTypes.string,
  getItemId: PropTypes.func,
  cardsPerRow: PropTypes.number,
  hidePagination: PropTypes.bool,
};

export default React.memo(CardTable);
